/**
 * Экран «Правка тренировки» — состав одной сессии из плана (раздел 8).
 *
 * Отвечает на просьбу «хочу корректировать тренировки». Здесь меняют то, что
 * принадлежит человеку: какие движения делать и сколько подходов с каким
 * диапазоном повторов. И не меняют то, что принадлежит движку — вес. Вес
 * приходит в `suggested_weight` уже посчитанным и округлённым под железо
 * (продуктовый принцип №1, раздел 2): клиент его показывает и не пересчитывает.
 * Поле веса не «забыли» — его тут не может быть, и об этом на экране написано
 * прямым текстом, иначе отсутствие поля читается как недоделка.
 *
 * Экран сознательно не такой, как «Активная тренировка». Туда заходят между
 * подходами с занятыми руками, и там запрещены поля ввода (раздел 13: в вебвью
 * Telegram на iOS клавиатура прыгает и перекрывает пол-экрана). Сюда заходят за
 * столом, планируя неделю, — поэтому поиск по справочнику сделан обычным
 * текстовым полем. Тач-зоны при этом те же 56 px: приложение одно, и привычка
 * к размеру кнопок не должна ломаться при переходе между экранами.
 *
 * Правка плана, в отличие от записи подхода, НЕ уходит в offline-очередь.
 * Подход — это факт, он обязан пережить подвал без связи. Правка плана —
 * намерение, и сервер после неё возвращает пересобранную сессию с новыми
 * весами: подделать этот ответ локально значило бы посчитать за движок.
 * Поэтому без связи экран честно говорит, что правка не ушла.
 */

import { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { NetworkError, api } from '../api/client'
import { useExercises, type Catalog } from '../api/exercises'
import { ExercisePicker } from '../components/ExercisePicker'
import type {
  Exercise,
  ExerciseAlternative,
  ExerciseBlock,
  Flag,
  Session,
  SessionAdditions,
} from '../api/types'
import { haptic } from '../auth/telegram'
import {
  ConfirmButton,
  Empty,
  ErrorBox,
  Flags,
  Loader,
  Stepper,
  formatDate,
  formatWeight,
} from '../components/ui'
import { useAsync } from '../hooks'

/**
 * Границы совпадают с серверными (`SessionExerciseIn`: sets 1..12,
 * reps 1..50). Свои, более узкие, клиент придумывать не вправе: он молча
 * запретил бы то, что движок принимает. Шире тоже нельзя — человек упёрся бы
 * в 422 уже после нажатия.
 */
const SETS_MIN = 1
const SETS_MAX = 12
const REPS_MIN = 1
const REPS_MAX = 50

/** Стартовое положение степперов у нового упражнения — те же значения, что
 *  сервер подставляет по умолчанию. Это не расчёт движка, а точка отсчёта. */
const DEFAULT_SCHEME: Scheme = { sets: 3, reps_min: 8, reps_max: 12 }

/** Схема подходов упражнения — единственное, что человек здесь задаёт. */
interface Scheme {
  sets: number
  reps_min: number
  reps_max: number
}

export default function SessionEdit() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const catalog = useExercises()

  // Роутер отдаёт строку: «/plan/abc» не должен превращаться в запрос
  // «/sessions/NaN» — сервер ответил бы 422 про тип, а человек прочитал бы
  // это как поломку тренировки.
  const id = Number(sessionId)
  const valid = Number.isInteger(id) && id > 0

  const state = useAsync<Session>(
    () =>
      valid
        ? api.get<Session>(`/sessions/${id}`)
        : Promise.reject(new Error('Неизвестная тренировка: проверь ссылку')),
    [id],
  )

  /**
   * Несохранённые схемы по exercise_id.
   *
   * Степперы не шлют запрос на каждый шаг: с 3 подходов до 8 это пять
   * запросов и пять пересборок сессии на сервере. Черновик живёт в клиенте,
   * пока человек не нажмёт «Сохранить», а после успеха выбрасывается — дальше
   * значения снова берутся из ответа сервера, и разойтись им негде.
   */
  const [drafts, setDrafts] = useState<Record<number, Scheme>>({})
  const [replacing, setReplacing] = useState<number | null>(null)

  /**
   * Подсказка замены: что предложить взамен движения, которое меняют.
   *
   * Считает сервер (`engine.alternatives`), а не экран: «похоже» — это знание
   * о движениях, и выдумывать его на клиенте значило бы завести второе
   * мнение, которое однажды разойдётся с первым.
   *
   * Список не обязателен: не приехал — человек выбирает поиском, как и
   * раньше. Поэтому ошибка тут молчит, а не занимает экран.
   */
  const [suggested, setSuggested] = useState<ExerciseAlternative[]>([])
  const [suggesting, setSuggesting] = useState(false)
  //: Какое движение меняют ПРЯМО СЕЙЧАС — для проверки в ответе запроса.
  //: Состояние в замыкании устарело бы: пока летит ответ, панель успевают
  //: закрыть или переключить на соседнее упражнение.
  const replacingRef = useRef<number | null>(null)
  const [adding, setAdding] = useState(false)
  const [addPick, setAddPick] = useState<Exercise | null>(null)
  const [addScheme, setAddScheme] = useState<Scheme>(DEFAULT_SCHEME)
  /**
   * Что добавить под задачу дня — считает сервер (`engine.additions`).
   *
   * Необязательно так же, как «Похожие движения»: не приехало — остаётся
   * поиск. Поэтому ошибка молчит, а ожидание короткое: подсказка, которая
   * приходит через пятнадцать секунд, приходит уже никому не нужной.
   */
  const [additions, setAdditions] = useState<SessionAdditions | null>(null)
  const [additionsLoading, setAdditionsLoading] = useState(false)
  /**
   * Предупреждения движка о последней правке — например, что добавка вывела
   * группу за верх недельного коридора. Сервер правку принял, но молча
   * проглотить такое экран не вправе.
   */
  const [notice, setNotice] = useState<{ at: number | 'add'; flags: Flag[] } | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * Отказ сервера привязан к месту, откуда его вызвали.
   *
   * Общая плашка вверху экрана не годится: «Убрать» на пятой карточке уводит
   * ответ за верхний край, и человек видит, что не произошло ничего. А не
   * произошло — потому что сервер отказал.
   */
  const [failure, setFailure] = useState<{ at: number | 'add'; text: string } | null>(null)

  const session = state.data

  /** Уже занятые движения: сервер отвечает на дубль 409, и лучше сказать это
   *  до нажатия, чем показать отказ после. */
  const taken = useMemo(
    () => new Set((session?.blocks ?? []).map((b) => b.exercise.id)),
    [session],
  )

  function dropDraft(exerciseId: number) {
    setDrafts((prev) => {
      if (!(exerciseId in prev)) return prev
      const next = { ...prev }
      delete next[exerciseId]
      return next
    })
  }

  /**
   * Общая обёртка правки: каждый эндпоинт возвращает пересобранную сессию,
   * её и кладём через `set()`. Перезапрашивать экран целиком незачем —
   * ответ уже содержит и новые блоки, и пересчитанные движком веса.
   */
  //: Номера последних запросов подсказок. Ответ старого запроса не должен ни
  //: подменить свежий, ни снять «подбираю…», пока свежий ещё в пути, ни
  //: вернуть подсказки в уже закрытую панель.
  const suggestSeq = useRef(0)
  const additionsSeq = useRef(0)
  //: Открыта ли панель добавления — для проверки после ответа правки: значение
  //: из замыкания к этому времени могло устареть.
  const addingRef = useRef(false)

  /**
   * `noticeAt` — где показать предупреждение сервера, если оно не там же, где
   * отказ: после замены карточка уже носит id нового движения.
   */
  async function mutate(
    at: number | 'add',
    run: () => Promise<Session>,
    after?: () => void,
    noticeAt: number | 'add' = at,
  ) {
    if (busy) return
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      const next = await run()
      state.set(next)
      haptic('light')
      if (next.flags && next.flags.length > 0) setNotice({ at: noticeAt, flags: next.flags })
      after?.()
      // Подсказки «под задачу дня» считались по составу ДО этой правки: «ещё
      // 4 подх.» и «группы нет в тренировке» могли стать неправдой.
      if (addingRef.current) void loadAdditions()
    } catch (e) {
      setFailure({ at, text: errorText(e) })
    } finally {
      setBusy(false)
    }
  }

  async function loadSuggestions(exerciseId: number): Promise<void> {
    const seq = ++suggestSeq.current
    setSuggesting(true)
    try {
      const found = await api.get<ExerciseAlternative[]>(
        `/sessions/${id}/exercises/${exerciseId}/alternatives`,
        { timeoutMs: 6000 },
      )
      // Панель могли успеть закрыть или переключить на другое движение, пока
      // шёл запрос: чужие подсказки под чужим упражнением хуже, чем никаких.
      if (seq === suggestSeq.current && replacingRef.current === exerciseId) setSuggested(found)
    } catch {
      // Подсказка необязательна: не приехала — остаётся поиск по справочнику.
    } finally {
      if (seq === suggestSeq.current) setSuggesting(false)
    }
  }

  async function loadAdditions(): Promise<void> {
    const seq = ++additionsSeq.current
    // Старые числа не показываем и секунды: пока идёт пересчёт, панель честно
    // пишет «подбираю…».
    setAdditions(null)
    setAdditionsLoading(true)
    try {
      const found = await api.get<SessionAdditions>(`/sessions/${id}/additions`, {
        timeoutMs: 6000,
      })
      if (seq === additionsSeq.current) setAdditions(found)
    } catch {
      if (seq === additionsSeq.current) setAdditions(null)
    } finally {
      if (seq === additionsSeq.current) setAdditionsLoading(false)
    }
  }

  /** Отказ прошлого добавления не должен пережить новую попытку. */
  function dropAddFailure(): void {
    setFailure((prev) => (prev?.at === 'add' ? null : prev))
  }

  /**
   * Панели «Заменить» и «Добавить» взаимоисключающие. Открытые разом, они
   * показали бы одно и то же движение в двух списках, и после любой правки
   * один из списков устарел бы — тап по нему дал бы 409.
   */
  function openAdding(): void {
    closeReplacing()
    setAddPick(null)
    setAddScheme(DEFAULT_SCHEME)
    dropAddFailure()
    setAdding(true)
    addingRef.current = true
    void loadAdditions()
  }

  /** Закрыть замену — вместе с запросом подсказок, который мог ещё лететь. */
  function closeReplacing(): void {
    setReplacing(null)
    replacingRef.current = null
    suggestSeq.current++
    setSuggested([])
    setSuggesting(false)
  }

  /** Закрыть добавление целиком — со схемой: иначе число подходов, выставленное
   *  под одно движение, молча перешло бы на следующее. */
  function closeAdding(): void {
    setAdding(false)
    addingRef.current = false
    additionsSeq.current++
    setAddPick(null)
    setAddScheme(DEFAULT_SCHEME)
    setAdditions(null)
    setAdditionsLoading(false)
    dropAddFailure()
  }

  const save = (exerciseId: number, scheme: Scheme) =>
    mutate(
      exerciseId,
      () =>
        api.patch<Session>(`/sessions/${id}/exercises/${exerciseId}`, {
          sets: scheme.sets,
          reps_min: scheme.reps_min,
          reps_max: scheme.reps_max,
        }),
      () => dropDraft(exerciseId),
    )

  const remove = (exerciseId: number) =>
    mutate(exerciseId, () => api.delete<Session>(`/sessions/${id}/exercises/${exerciseId}`), () => {
      dropDraft(exerciseId)
      closeReplacing()
    })

  const replace = (exerciseId: number, next: Exercise) =>
    mutate(
      exerciseId,
      () =>
        api.post<Session>(`/sessions/${id}/exercises/${exerciseId}/replace`, {
          exercise_id: next.id,
        }),
      () => {
        dropDraft(exerciseId)
        closeReplacing()
      },
      // Отказ — под прежней карточкой: она осталась на месте. Предупреждение —
      // под новой: прежней после замены на экране уже нет.
      next.id,
    )

  const add = (exercise: Exercise, scheme: Scheme) =>
    mutate(
      'add',
      () =>
        api.post<Session>(`/sessions/${id}/exercises`, {
          exercise_id: exercise.id,
          sets: scheme.sets,
          reps_min: scheme.reps_min,
          reps_max: scheme.reps_max,
        }),
      () => closeAdding(),
    )

  // --- Загрузка ------------------------------------------------------------
  if (state.loading && !session) {
    return (
      <div className="screen">
        <Loader label="Открываю тренировку…" />
      </div>
    )
  }

  // --- Ошибка --------------------------------------------------------------
  if (!session) {
    return (
      <div className="screen">
        <BackButton onClick={() => navigate('/plan')} />
        <h1>Тренировка</h1>
        {/* Повторять запрос по битой ссылке бессмысленно: «/plan/abc» и на
            десятый раз останется «/plan/abc». А про связь пишем только тогда,
            когда дело в связи, — на 404 эта строка увела бы человека искать
            сеть вместо ошибки в ссылке. */}
        <ErrorBox error={state.error} onRetry={valid ? () => void state.reload() : undefined} />
        {state.error instanceof NetworkError && (
          <p className="faint">
            Состав тренировки живёт на сервере: без связи его не открыть и не поправить.
          </p>
        )}
      </div>
    )
  }

  const dateLine = [
    `Неделя ${session.week_no}, день ${session.day_no}`,
    session.planned_for_date ? capitalize(formatDate(session.planned_for_date)) : null,
  ]
    .filter(Boolean)
    .join(' · ')

  const head = (
    <>
      <BackButton onClick={() => navigate('/plan')} />
      <h1>{session.label}</h1>
      <div className="muted">{dateLine}</div>
      {(session.is_deload || session.is_calibration) && (
        <div className="row" style={{ marginTop: 8 }}>
          {session.is_deload && <span className="pill warn">Разгрузка</span>}
          {session.is_calibration && <span className="pill accent">Калибровка</span>}
        </div>
      )}
    </>
  )

  // --- Завершённая тренировка ---------------------------------------------
  // Сервер ответит на любую правку 409, но упираться в отказ ПОСЛЕ нажатия —
  // плохой способ узнать правило. Поэтому органов управления здесь нет вовсе,
  // а состав показан как есть: это уже история, и её читают.
  if (session.status === 'completed') {
    return (
      <div className="screen">
        {head}
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ fontSize: 17, marginBottom: 6 }}>Тренировка завершена — план не правится</div>
          <div className="muted">
            План завершённой тренировки — это уже история, а не намерение. По нему движок
            считает прогрессию: подвинешь план задним числом — поедут веса в следующих
            тренировках.
          </div>
          <div className="faint" style={{ marginTop: 8 }}>
            Ошибку в записанном подходе можно поправить в «Истории»: там лежат факты, здесь — план.
          </div>
        </div>
        {session.blocks.length === 0 ? (
          <Empty title="В этой тренировке не было упражнений" />
        ) : (
          session.blocks.map((block) => (
            <div className="card" key={block.exercise.id}>
              <BlockHead block={block} />
            </div>
          ))
        )}
      </div>
    )
  }

  // --- Правка --------------------------------------------------------------
  return (
    <div className="screen">
      {head}

      {/* Статус экрана — факт с сервера, а не оформление: правка идущей и
          пропущенной тренировки разрешена (сервер запрещает только
          завершённую), но человек должен понимать, что именно он правит. */}
      {session.status === 'in_progress' && (
        <div className="se-mark warn" style={{ marginTop: 12 }}>
          Тренировка уже идёт. Правки увидишь на экране подходов — записанное останется на месте.
        </div>
      )}
      {session.status === 'skipped' && (
        <div className="se-mark warn" style={{ marginTop: 12 }}>
          Тренировка отмечена пропущенной. План поправить можно, но в прогресс она попадёт
          только по записанным подходам.
        </div>
      )}

      <h2>Состав</h2>

      {session.blocks.length === 0 ? (
        <Empty
          title="В тренировке пока нет упражнений"
          hint="Добавь первое — кнопка ниже."
        />
      ) : (
        session.blocks.map((block) => (
          <BlockCard
            key={block.exercise.id}
            block={block}
            draft={drafts[block.exercise.id] ?? schemeOf(block)}
            busy={busy}
            failure={failure?.at === block.exercise.id ? failure.text : ''}
            notice={notice?.at === block.exercise.id ? notice.flags : []}
            catalog={catalog}
            taken={taken}
            replaceOpen={replacing === block.exercise.id}
            suggested={replacing === block.exercise.id ? suggested : []}
            suggesting={replacing === block.exercise.id && suggesting}
            onDraft={(next) =>
              setDrafts((prev) => ({ ...prev, [block.exercise.id]: next }))
            }
            onSave={(next) => void save(block.exercise.id, next)}
            onReset={() => dropDraft(block.exercise.id)}
            onRemove={() => void remove(block.exercise.id)}
            onToggleReplace={() => {
              const opening = replacing !== block.exercise.id
              closeReplacing()
              if (!opening) return
              if (adding) closeAdding()
              setReplacing(block.exercise.id)
              replacingRef.current = block.exercise.id
              void loadSuggestions(block.exercise.id)
            }}
            onReplace={(next) => void replace(block.exercise.id, next)}
          />
        ))
      )}

      <h2>Добавить упражнение</h2>

      {notice?.at === 'add' && <Flags flags={notice.flags} />}

      {!adding ? (
        <button className="btn wide" onClick={openAdding}>
          Добавить упражнение
        </button>
      ) : (
        <div className="card">
          {addPick ? (
            <>
              <div className="row between">
                <div className="ex-name" style={{ minWidth: 0 }}>{addPick.name}</div>
                <button
                  className="btn ghost"
                  onClick={() => {
                    setAddPick(null)
                    setAddScheme(DEFAULT_SCHEME)
                    dropAddFailure()
                  }}
                >
                  Другое
                </button>
              </div>
              <div className="faint" style={{ marginTop: 6 }}>
                Вес движок подставит сам, когда тренировка откроется: здесь только схема.
              </div>
              <SchemeFields
                scheme={addScheme}
                onChange={setAddScheme}
              />
              {addScheme.reps_min > addScheme.reps_max && (
                <div className="se-fail">
                  Нижняя граница повторов больше верхней — подвинь одну из них.
                </div>
              )}
              {failure?.at === 'add' && <div className="se-fail">{failure.text}</div>}
              <button
                className="btn wide primary"
                disabled={busy || addScheme.reps_min > addScheme.reps_max}
                onClick={() => void add(addPick, addScheme)}
              >
                {busy ? 'Добавляю…' : 'Добавить в тренировку'}
              </button>
            </>
          ) : (
            <ExercisePicker
              catalog={catalog}
              taken={taken}
              onPick={setAddPick}
              disabled={busy}
              pickLabel="Добавить это"
              hint={additions?.message}
              featuredTitle="Под задачу дня"
              featured={(additions?.items ?? []).map((item) => ({
                exercise: item.exercise,
                reason: item.reason,
              }))}
              featuredLoading={additionsLoading}
              groups={additions?.status === 'ok' ? additions.groups : undefined}
            />
          )}
          <button
            className="btn wide ghost"
            style={{ marginTop: 10 }}
            onClick={closeAdding}
          >
            Отмена
          </button>
        </div>
      )}
    </div>
  )
}

// --------------------------------------------------------------------------- //
// Карточка упражнения
// --------------------------------------------------------------------------- //

interface BlockCardProps {
  block: ExerciseBlock
  draft: Scheme
  busy: boolean
  /** Отказ сервера по ЭТОМУ упражнению, его же словами. Пустая строка — всё цело. */
  failure: string
  /**
   * Предупреждения сервера о правке ЭТОГО упражнения — там же, где отказ, и
   * по той же причине: над списком их унесло бы за верхний край экрана.
   */
  notice: Flag[]
  catalog: Catalog
  taken: Set<number>
  replaceOpen: boolean
  /** Подсказанные замены — то, что предложил сервер. */
  suggested: ExerciseAlternative[]
  suggesting: boolean
  onDraft: (next: Scheme) => void
  onSave: (next: Scheme) => void
  onReset: () => void
  onRemove: () => void
  onToggleReplace: () => void
  onReplace: (next: Exercise) => void
}

function BlockCard({
  block,
  draft,
  busy,
  failure,
  notice,
  catalog,
  taken,
  replaceOpen,
  suggested,
  suggesting,
  onDraft,
  onSave,
  onReset,
  onRemove,
  onToggleReplace,
  onReplace,
}: BlockCardProps) {
  const server = schemeOf(block)
  const dirty =
    draft.sets !== server.sets ||
    draft.reps_min !== server.reps_min ||
    draft.reps_max !== server.reps_max
  const invalid = draft.reps_min > draft.reps_max
  const uniform = isUniformScheme(block)
  // Подходов нет — значит движение исключено движком (обычно из-за боли).
  // Схему такому упражнению не задают: его либо убирают, либо заменяют.
  //
  // Разные подходы (процентная программа) — второй случай без степперов, и
  // причина серьёзнее вёрстки. PATCH принимает ОДИН диапазон повторов на
  // упражнение и раскатывает его по всем подходам: сохранение даже без правки
  // диапазона превратило бы 1×5 / 1×5 / 1×5+ / 5×10 в восемь одинаковых
  // подходов. Программу, которую человек выбрал, экран правки молча
  // переписывать не должен.
  const editable = block.sets.length > 0 && uniform

  return (
    <div className="card">
      <BlockHead block={block} />

      {/* У исключённого из-за боли движения подходов нет — объяснять, откуда
          берётся вес, там нечего: шапка уже сказала главное. */}
      {block.sets.length > 0 && (
        <div className="faint" style={{ marginTop: 6 }}>
          {uniform
            ? 'Вес подставляет движок по твоим прошлым подходам — руками он здесь не меняется.'
            : 'Вес считает программа: доля от твоего тренировочного максимума. Руками он здесь не меняется.'}
        </div>
      )}

      {block.sets.length > 0 && !uniform && (
        <div className="se-mark warn">
          Схему задаёт программа: у подходов разный вес и разные повторы. Здесь она не
          правится — сервер выровнял бы все подходы под первый, и программа перестала бы
          быть собой. Это движение можно убрать или заменить, а схему целиком меняют
          сменой программы.
        </div>
      )}

      {editable && (
        <>
          <SchemeFields scheme={draft} onChange={onDraft} />

          {invalid && (
            <div className="se-fail">
              Нижняя граница повторов больше верхней — подвинь одну из них.
            </div>
          )}

          {/* Сервер при уменьшении срезает ПОСЛЕДНИЕ подходы (первые могли быть
              уже выполнены). Ровно на последнем и стоит пометка «на максимум» —
              а это суть GZCLP и 5/3/1: по нему движок решает, двигать ли вес.
              Молча потерять её нельзя. */}
          {cutsMarkedSets(block, draft.sets) && (
            <div className="se-mark warn">
              Сервер срежет последние подходы — а пометка программы стоит именно на них.
            </div>
          )}

          {dirty && (
            <div className="se-actions">
              <button className="btn ghost" disabled={busy} onClick={onReset}>
                Отменить
              </button>
              <button
                className="btn primary"
                disabled={busy || invalid}
                onClick={() => onSave(draft)}
              >
                {busy ? 'Сохраняю…' : 'Сохранить'}
              </button>
            </div>
          )}
        </>
      )}

      <div className="se-actions">
        <button className="btn" disabled={busy} onClick={onToggleReplace}>
          {replaceOpen ? 'Не заменять' : 'Заменить'}
        </button>
        {/* Пока идёт другой запрос, `mutate` всё равно откажет — но молча.
            Проглоченное подтверждение удаления читается как поломка, поэтому
            на время запроса кнопка честно выключена. */}
        {busy ? (
          <button className="btn danger" type="button" disabled>
            Убрать
          </button>
        ) : (
          <ConfirmButton
            className="btn danger"
            confirmLabel="Точно убрать?"
            onConfirm={onRemove}
          >
            Убрать
          </ConfirmButton>
        )}
      </div>

      {failure && <div className="se-fail">{failure}</div>}
      {notice.length > 0 && <Flags flags={notice} />}

      {/* Страх нажать «Убрать» дороже самой кнопки: пишем до нажатия, что
          факты не пропадут. Сервер удаляет только план. */}
      <div className="faint" style={{ marginTop: 8 }}>
        «Убрать» стирает план на это движение. Записанные подходы остаются в истории и
        продолжают считаться в прогрессе.
      </div>

      {replaceOpen && (
        <div className="se-replace">
          {/* Что именно делает сервер в `replace_exercise`: подходы остаются,
              `percent_of_e1rm` обнуляется, `note` очищается, `amrap` живёт
              дальше. Для процентного блока это значит больше: без процентов
              вес назначает движок, и подходы становятся одинаковыми. Обещать
              «схема перейдёт» в этом случае было бы неправдой. */}
          <div className="faint" style={{ marginBottom: 8 }}>
            {uniform
              ? 'Число подходов и диапазон повторов перейдут на новое движение. Текстовые пометки программы сервер очистит — они были про прежнее; отметка «на максимум» останется, а вес движок посчитает по твоей истории в новом упражнении.'
              : 'Число подходов перейдёт, но проценты сервер сбросит: они считались от максимума прежнего движения. Дальше вес назначает движок по твоей истории, и подходы станут одинаковыми — процентной схемы у нового движения не будет.'}
          </div>
          {/* Похожие движения — первым делом. Человек меняет упражнение не
              от хорошей жизни: стойка занята, тренажёр сломан, плечо не
              идёт. В этот момент искать по справочнику из сорока строк — не
              то, чем он хочет заниматься. Поиск остаётся ниже: подсказка не
              заменяет выбор, а снимает с него самую частую работу. */}
          <ExercisePicker
            catalog={catalog}
            taken={taken}
            onPick={onReplace}
            disabled={busy}
            pickLabel="Заменить на это"
            featuredTitle="Похожие движения"
            featured={suggested.map((item) => ({ exercise: item.exercise, reason: item.reason }))}
            featuredLoading={suggesting}
          />
        </div>
      )}
    </div>
  )
}

/** Шапка карточки: что делать, по какой схеме и с каким весом. */
function BlockHead({ block }: { block: ExerciseBlock }) {
  // В процентных программах (5/3/1) у подходов одного движения РАЗНЫЕ вес и
  // повторы: 1×5 от 65%, 1×5 от 75%, 1×5+ от 85%, 5×10 от 50%. Одна строка
  // «8 подходов × 5» и один крупный вес соврали бы про семь подходов из
  // восьми, поэтому такой блок расписывается по подходам.
  const uniform = isUniformScheme(block)
  const rows = uniform ? [] : setRows(block)
  return (
    <>
      <div className="row between">
        <div style={{ minWidth: 0 }}>
          <div className="ex-name">{block.exercise.name}</div>
          <div className="faint tnum">{schemeText(block)}</div>
        </div>
        <div className="ex-weight tnum">
          {block.suggested_weight === null ? (
            '—'
          ) : (
            <>
              {formatWeight(block.suggested_weight)}
              <span className="ex-unit"> кг</span>
            </>
          )}
        </div>
      </div>

      {/* Разбор по подходам. Цифры взяты из ответа сервера как есть: вес —
          `target_weight` подхода, повторы — его диапазон. Подряд идущие
          одинаковые подходы сведены в строку «4–8» — это только вёрстка,
          значения не трогаются. */}
      {rows.length > 0 && (
        <>
          <div className="se-sets">
            {rows.map((r) => (
              <div className="se-set" key={r.from}>
                <span className="se-set-no tnum">
                  {r.from === r.to ? r.from : `${r.from}–${r.to}`}
                </span>
                <span className="tnum">
                  {r.weight === null ? '—' : `${formatWeight(r.weight)} кг`} × {r.reps} @ RPE{' '}
                  {rpeText(r.rpe)}
                  {r.amrap ? ' · на максимум' : ''}
                </span>
                {r.note && <span className="faint">{r.note}</span>}
              </div>
            ))}
          </div>
          {block.suggested_weight !== null && (
            <div className="faint" style={{ marginTop: 6 }}>
              Крупно показан самый тяжёлый подход — у остальных вес свой.
            </div>
          )}
        </>
      )}

      {/* Причина веса — текст движка. Показывается как есть (принцип честности,
          раздел 2): «первая тренировка, вес по калибровке» объясняет прочерк
          лучше любой формулировки, придуманной клиентом. */}
      {block.reason && (
        <div className="faint" style={{ marginTop: 6 }}>
          {block.reason}
        </div>
      )}

      {block.has_active_pain && (
        <div className="se-mark warn">
          Движение исключено из-за боли, подходов в плане нет. Приложение не медицинский
          сервис и советов не даёт: если боль не проходит — покажись врачу.
        </div>
      )}

      {/* Для разобранного по подходам блока пометки уже стоят в своих строках —
          повторять их сводкой значит писать одно и то же дважды. */}
      {uniform &&
        marks(block).map((text) => (
          <div className="se-mark" key={text}>
            {text}
          </div>
        ))}
    </>
  )
}

// --------------------------------------------------------------------------- //
// Схема подходов и поиск по справочнику
// --------------------------------------------------------------------------- //

/**
 * Три степпера: подходы отдельной строкой, диапазон повторов парой.
 * Три в ряд не помещаются — кнопки −/+ по 56 px, и на экране 375 px третья
 * колонка ужала бы их до размера, в который не попасть.
 */
function SchemeFields({
  scheme,
  onChange,
}: {
  scheme: Scheme
  onChange: (next: Scheme) => void
}) {
  return (
    <div className="se-fields">
      <Stepper
        label="Подходы"
        value={scheme.sets}
        min={SETS_MIN}
        max={SETS_MAX}
        onChange={(sets) => onChange({ ...scheme, sets })}
      />
      <div className="se-pair">
        <Stepper
          label="Повторы от"
          value={scheme.reps_min}
          min={REPS_MIN}
          max={REPS_MAX}
          onChange={(reps_min) => onChange({ ...scheme, reps_min })}
        />
        <Stepper
          label="Повторы до"
          value={scheme.reps_max}
          min={REPS_MIN}
          max={REPS_MAX}
          onChange={(reps_max) => onChange({ ...scheme, reps_max })}
        />
      </div>
    </div>
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="btn ghost se-back" onClick={onClick}>
      ← Назад
    </button>
  )
}

// --------------------------------------------------------------------------- //
// Форматирование. Считать здесь нечего — только показывать пришедшее.
// --------------------------------------------------------------------------- //

/**
 * Схема упражнения из ответа сервера.
 *
 * Диапазон берётся у первого подхода, а не сводится по всем: движок выдаёт на
 * упражнение одно назначение и штампует им все подходы блока. Сводить границы
 * самим значило бы считать на клиенте (принцип №1).
 */
function schemeOf(block: ExerciseBlock): Scheme {
  const first = block.sets[0]
  return {
    sets: block.sets.length > 0 ? block.sets.length : DEFAULT_SCHEME.sets,
    reps_min: first?.target_reps_min ?? DEFAULT_SCHEME.reps_min,
    reps_max: first?.target_reps_max ?? DEFAULT_SCHEME.reps_max,
  }
}

/**
 * Одинаковы ли подходы блока.
 *
 * В обычном блоке движок штампует одно назначение на все подходы: вес,
 * диапазон и RPE у них совпадают, и «3 подхода × 5–8» описывает блок целиком.
 * В процентной программе (5/3/1) у каждого подхода свой процент от
 * тренировочного максимума и свои повторы — там такая строка врёт.
 *
 * Сравниваются только цифры назначения. Пометки `amrap`/`note` в расчёт не
 * идут: они и в обычном блоке стоят не на всех подходах, а схему не меняют.
 */
function isUniformScheme(block: ExerciseBlock): boolean {
  const first = block.sets[0]
  if (!first) return true
  return block.sets.every(
    (s) =>
      s.target_weight === first.target_weight &&
      s.target_reps_min === first.target_reps_min &&
      s.target_reps_max === first.target_reps_max &&
      s.target_rpe === first.target_rpe,
  )
}

/** Строка разбора: подряд идущие одинаковые подходы сведены в диапазон. */
interface SetRow {
  from: number
  to: number
  weight: number | null
  reps: string
  rpe: number
  amrap: boolean
  note: string
}

/**
 * Разбор блока по подходам — для тех блоков, где подходы разные.
 *
 * Ничего не считает: вес берётся из `target_weight` подхода, повторы из его
 * диапазона. Единственное действие — склейка соседних одинаковых подходов в
 * «4–8», чтобы восемь строк не занимали пол-экрана.
 */
function setRows(block: ExerciseBlock): SetRow[] {
  const out: SetRow[] = []
  for (const s of block.sets) {
    const reps = repsText(s.target_reps_min, s.target_reps_max)
    const prev = out[out.length - 1]
    if (
      prev &&
      prev.weight === s.target_weight &&
      prev.reps === reps &&
      prev.rpe === s.target_rpe &&
      prev.amrap === s.amrap &&
      prev.note === s.note
    ) {
      prev.to = s.set_no
      continue
    }
    out.push({
      from: s.set_no,
      to: s.set_no,
      weight: s.target_weight,
      reps,
      rpe: s.target_rpe,
      amrap: s.amrap,
      note: s.note,
    })
  }
  return out
}

/** «5» или «5–8». */
function repsText(min: number, max: number): string {
  return min === max ? String(min) : `${min}–${max}`
}

/** «3 подхода × 5–8 @ RPE 8». Для разных подходов — только их число. */
function schemeText(block: ExerciseBlock): string {
  const first = block.sets[0]
  if (!first) return 'Подходов в плане нет'
  const count = block.sets.length
  const word = plural(count, ['подход', 'подхода', 'подходов'])
  if (!isUniformScheme(block)) return `${count} ${word}, каждый со своим весом`
  return `${count} ${word} × ${repsText(first.target_reps_min, first.target_reps_max)} @ RPE ${rpeText(first.target_rpe)}`
}

/**
 * Пометки программы: подходы «на максимум» и текстовые примечания движка.
 *
 * Это не украшение. В GZCLP и 5/3/1 именно AMRAP-подход решает, двигать вес или
 * нет, — тренировка без этой пометки перестаёт быть той программой, которую
 * человек выбрал. Показываем всегда, в том числе на завершённой тренировке.
 */
function marks(block: ExerciseBlock): string[] {
  const out: string[] = []

  const amrap = block.sets.filter((s) => s.amrap).map((s) => s.set_no)
  if (amrap.length > 0) {
    const head = amrap.length === 1 ? 'Подход' : 'Подходы'
    const tail = amrap.length === 1 ? 'По нему' : 'По ним'
    out.push(
      `${head} ${amrap.join(', ')} — на максимум повторений. ${tail} движок решает, двигать ли вес.`,
    )
  }

  // Один и тот же текст стоит на всех подходах блока — показываем один раз.
  for (const note of new Set(block.sets.map((s) => s.note).filter((n) => n.trim() !== ''))) {
    out.push(note)
  }
  return out
}

/**
 * Срежет ли уменьшение подходов те, на которых стоят пометки программы.
 *
 * Сервер удаляет именно последние подходы: первые могли быть уже выполнены.
 * А пометка «на максимум» в 5/3/1 стоит как раз на последнем.
 */
function cutsMarkedSets(block: ExerciseBlock, nextSets: number): boolean {
  if (nextSets >= block.sets.length) return false
  return block.sets.slice(nextSets).some((s) => s.amrap || s.note.trim() !== '')
}

/** RPE без хвостового нуля: 8, 8.5. */
function rpeText(rpe: number): string {
  return Number.isInteger(rpe) ? String(rpe) : rpe.toFixed(1)
}

/** «1 подход», «3 подхода», «5 подходов»: кривое склонение читается как баг. */
function plural(n: number, forms: [string, string, string]): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return forms[2]
  const mod10 = n % 10
  if (mod10 === 1) return forms[0]
  if (mod10 >= 2 && mod10 <= 4) return forms[1]
  return forms[2]
}

function capitalize(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s
}

/**
 * Текст ошибки.
 *
 * Сеть и отказ сервера здесь принципиально разные вещи, и путать их нельзя:
 * подход из очереди доедет сам, а правка плана не уходит в очередь вовсе.
 * Отказ сервера показываем его же словами — «Это упражнение уже есть в
 * тренировке» объясняет себя лучше, чем любой пересказ.
 */
function errorText(e: unknown): string {
  if (e instanceof NetworkError) {
    return 'Нет связи. Правка не ушла — на сервере ничего не изменилось.'
  }
  return e instanceof Error ? e.message : 'Что-то пошло не так'
}
